/**
 * Process Insurance Actor
 *
 * Handles insurance verification and processing logic
 */

import { InsurancesStatusEnum } from "@frontend/lib/graphql/generated-gql";
import { getInsuranceStatuses } from "@frontend/lib/actions/actions";
import type {
  FormInsuranceData,
  InsuranceStatus,
} from "@frontend/lib/schemas/clientSchema";
import { RequestSource } from "@frontend/lib/schemas/clientSchema";
import type { Consumer } from "@frontend/lib/stores/slices/consumerSlice";
import { ShouldChangeToPendingPricesMode } from "@frontend/lib/utils/pendingPricesHelper";
import { fromPromise } from "xstate";

export interface ProcessInsuranceInput {
  consumer: Consumer;
  insurances: FormInsuranceData[];
  orgId: number;
}

export type InsuranceOutcome =
  | "active"
  | "unverified"
  | "inactive"
  | "out_of_network";

export interface ProcessInsuranceOutput {
  outcome: InsuranceOutcome;
  insuranceStates: InsuranceStatus[];
  pendingPricesMode: boolean;
}

/**
 * Classify insurance verification outcome based on primary insurance status
 *
 * Priority order:
 * 1. Inactive - insurance is inactive/expired
 * 2. Out of network - insurance is out of network or explicitly marked as such
 * 3. Unverified - insurance could not be verified
 * 4. Active - insurance is active and in-network
 */
function classifyOutcome(statuses: InsuranceStatus[]): InsuranceOutcome {
  console.log(
    "[classifyOutcome] Raw statuses input:",
    JSON.stringify(statuses, null, 2),
  );

  if (!statuses || statuses.length === 0) {
    console.log(
      "[classifyOutcome] → No statuses returned, defaulting to 'unverified'",
    );
    return "unverified";
  }

  const primaryInsurance = statuses[0];

  console.log("[classifyOutcome] Primary insurance values:", {
    status: primaryInsurance.status,
    statusType: typeof primaryInsurance.status,
    network: primaryInsurance.network,
    networkType: typeof primaryInsurance.network,
    hasBenefits: primaryInsurance.hasBenefits,
    hasBenefitsType: typeof primaryInsurance.hasBenefits,
    error: primaryInsurance.error,
    payer: primaryInsurance.payer,
    memberId: primaryInsurance.memberId,
    state: primaryInsurance.state,
    planName: primaryInsurance.planName,
  });

  console.log("[classifyOutcome] Guard checks:", {
    isInactive: primaryInsurance.status === InsurancesStatusEnum.Inactive,
    isManuallyVerifiedExpired:
      primaryInsurance.status === InsurancesStatusEnum.ManuallyVerifiedExpired,
    isNetworkOUT: primaryInsurance.network === "OUT",
    isActiveOutOfNetwork:
      primaryInsurance.status === InsurancesStatusEnum.ActiveOutOfNetwork,
    isUnverified: primaryInsurance.status === InsurancesStatusEnum.Unverified,
    isManuallyVerified:
      primaryInsurance.status === InsurancesStatusEnum.Manuallyverified,
    isActiveBenefitsNotReturned:
      primaryInsurance.status ===
      InsurancesStatusEnum.ActiveBenefitsNotReturned,
    isActiveWithoutBenefits:
      primaryInsurance.status === InsurancesStatusEnum.Active &&
      !primaryInsurance.hasBenefits,
    isActiveInNetworkWithBenefits:
      primaryInsurance.status === InsurancesStatusEnum.Active &&
      primaryInsurance.network === "IN" &&
      primaryInsurance.hasBenefits,
  });

  if (
    primaryInsurance.status === InsurancesStatusEnum.Inactive ||
    primaryInsurance.status === InsurancesStatusEnum.ManuallyVerifiedExpired
  ) {
    console.log(
      "[classifyOutcome] → INACTIVE (status:",
      primaryInsurance.status,
      ")",
    );
    return "inactive";
  }

  if (
    primaryInsurance.network === "OUT" ||
    primaryInsurance.status === InsurancesStatusEnum.ActiveOutOfNetwork
  ) {
    console.log(
      "[classifyOutcome] → OUT_OF_NETWORK (network:",
      primaryInsurance.network,
      ", status:",
      primaryInsurance.status,
      ")",
    );
    return "out_of_network";
  }

  if (
    primaryInsurance.status === InsurancesStatusEnum.Unverified ||
    primaryInsurance.status === InsurancesStatusEnum.Manuallyverified ||
    primaryInsurance.status ===
      InsurancesStatusEnum.ActiveBenefitsNotReturned ||
    (primaryInsurance.status === InsurancesStatusEnum.Active &&
      !primaryInsurance.hasBenefits)
  ) {
    console.log(
      "[classifyOutcome] → UNVERIFIED (status:",
      primaryInsurance.status,
      ", hasBenefits:",
      primaryInsurance.hasBenefits,
      ")",
    );
    return "unverified";
  }

  if (
    primaryInsurance.status === InsurancesStatusEnum.Active &&
    primaryInsurance.network === "IN" &&
    primaryInsurance.hasBenefits
  ) {
    console.log("[classifyOutcome] → ACTIVE (all conditions met)");
    return "active";
  }

  console.log(
    "[classifyOutcome] → UNVERIFIED (fell through all guards — no condition matched)",
    {
      status: primaryInsurance.status,
      network: primaryInsurance.network,
      hasBenefits: primaryInsurance.hasBenefits,
    },
  );
  return "unverified";
}

/**
 * Core processing logic — exported as a plain async function for direct
 * testing, and wrapped by `processInsuranceActorLogic` for XState usage.
 */
export async function processInsuranceActor({
  input,
}: {
  input: ProcessInsuranceInput;
}): Promise<ProcessInsuranceOutput> {
  console.log(
    "[InsuranceConnector:processInsurance] Actor STARTED with input:",
    {
      consumer: {
        fname: input.consumer.fname,
        lname: input.consumer.lname,
        dob: input.consumer.dob,
      },
      insuranceCount: input.insurances.length,
      orgId: input.orgId,
    },
  );

  const { consumer, insurances, orgId } = input;

  try {
    console.log(
      "[InsuranceConnector:processInsurance] Calling getInsuranceStatuses...",
    );
    const statuses = await getInsuranceStatuses(
      consumer,
      insurances,
      orgId,
      RequestSource.STOREFRONT,
      null,
      undefined,
      undefined,
    );

    console.log(
      "[InsuranceConnector:processInsurance] getInsuranceStatuses returned:",
      {
        statusCount: statuses?.length,
        statuses: statuses?.map((s: InsuranceStatus) => ({
          status: s.status,
          network: s.network,
          hasBenefits: s.hasBenefits,
        })),
      },
    );

    const outcome = classifyOutcome(statuses);
    const pendingPricesMode = ShouldChangeToPendingPricesMode(statuses);

    console.log("[InsuranceConnector:processInsurance] Actor RESOLVING with:", {
      outcome,
      pendingPricesMode,
    });

    return {
      outcome,
      insuranceStates: statuses,
      pendingPricesMode,
    };
  } catch (error) {
    console.error(
      "[InsuranceConnector:processInsurance] Actor REJECTING with error:",
      error,
    );
    throw error;
  }
}

/**
 * XState actor logic wrapping the core function
 */
export const processInsuranceActorLogic = fromPromise<
  ProcessInsuranceOutput,
  ProcessInsuranceInput
>(processInsuranceActor);
